// MODE DÉMO : ce que comprend un compte ouvert depuis /demo (1 projet, 5 études,
// 50 actions IA — le projet exemple en plus), et où il en est.
//
// Trois affichages, rien de plus : un badge « Démo » dans l'en-tête, une ligne
// discrète là où l'on crée un projet ou une étude, et le détail sur la page
// Compte. Hors démo, aucun ne s'affiche. Le serveur applique les limites ; ceci
// n'est que l'information.
import { useBootstrap, useDemoUsage } from '../api/queries';
import type { DemoCounter } from '../api/types';
import { Link } from '../router';
import './demo.css';

type Kind = 'projects' | 'studies' | 'aiActions';

const NOUNS: Record<Kind, [string, string]> = {
  projects: ['projet', 'projets'],
  studies: ['étude', 'études'],
  aiActions: ['action IA', 'actions IA'],
};

/** « 3 études sur 5 », « 1 projet sur 1 », « 12 actions IA sur 50 ». */
export function counterLabel(kind: Kind, c: DemoCounter) {
  const [one, many] = NOUNS[kind];
  return `${c.used} ${c.used > 1 ? many : one} sur ${c.limit}`;
}

const HOW_TO_CONTINUE =
  'Pour aller plus loin, écrivez-nous avec le bouton « Un retour ? » en bas à droite : nous vous ouvrirons l’offre complète.';

/** Badge de l'en-tête, vers le détail sur la page Compte. */
export function DemoBadge() {
  const usage = useDemoUsage();
  const u = usage.data;
  if (!u?.demo) return null;
  const title = (['projects', 'studies', 'aiActions'] as const).map((k) => counterLabel(k, u[k])).join(' · ');
  return (
    <Link to="/compte" className="demo-badge t-mono">
      <span title={`Mode démo — ${title}`}>Démo</span>
    </Link>
  );
}

/** Une ligne là où l'on crée : « 3 études sur 5 · mode démo ». Rien dans le
 *  projet exemple, dont les études sont offertes. */
export function DemoQuota({ kind, projectId }: { kind: 'projects' | 'studies'; projectId?: string }) {
  const usage = useDemoUsage();
  const bootstrap = useBootstrap(Boolean(projectId));
  const u = usage.data;
  if (!u?.demo) return null;
  if (projectId) {
    const project = bootstrap.data?.clients.flatMap((c) => c.projets).find((p) => p.id === projectId);
    if (!project || project.real) return null;
  }
  const c = u[kind];
  const full = c.used >= c.limit;
  return (
    <p className={full ? 'demo-quota is-full' : 'demo-quota'}>
      {counterLabel(kind, c)} · mode démo{full ? ' — limite atteinte' : ''}
    </p>
  );
}

/** Détail sur la page Compte. */
export function DemoUsageSection() {
  const usage = useDemoUsage();
  const u = usage.data;
  if (!u?.demo) return null;
  return (
    <section className="zy-card account-section" aria-labelledby="demo-usage-title">
      <div className="account-head">
        <div>
          <h2 className="t-h3" id="demo-usage-title">Mode démo</h2>
          <p className="account-lede">
            Votre compte est en mode démo. Le projet exemple est offert en plus : ni lui ni ses études ne comptent.
          </p>
        </div>
      </div>
      <ul className="demo-meters">
        {(['projects', 'studies', 'aiActions'] as const).map((k) => {
          const c = u[k];
          const share = c.limit > 0 ? Math.min(1, c.used / c.limit) : 1;
          return (
            <li key={k} className={c.used >= c.limit ? 'is-full' : undefined}>
              <span>{counterLabel(k, c)}</span>
              <span className="demo-meter" aria-hidden="true">
                <span style={{ width: `${Math.round(share * 100)}%` }} />
              </span>
            </li>
          );
        })}
      </ul>
      <p className="account-meta">{HOW_TO_CONTINUE}</p>
    </section>
  );
}
